// Shopify supplies the fingerprinted URL so editorial updates cannot reuse a stale CDN module.
const editorialModule = typeof document === 'undefined' ? './cx-brand-editorial.js' : document.querySelector('brand-route[data-editorial-module]')?.dataset.editorialModule || './cx-brand-editorial.js';
const {setupBrandEditorial, updateBrandCollection} = await import(editorialModule);
/** State is derived from server-rendered Shopify cards, never a copied catalogue. */
export function readRouteState(search, validKeys) {
  const params = new URLSearchParams(search);
  const selected = (params.get('marcas') || '').split(',').filter(key => validKeys.includes(key)).slice(0, 1);
  return { selected, available: params.get('disponibles') === '1' };
}

export function selectBrand(selected, key) {
  if (!key) return [];
  return selected[0] === key ? [] : [key];
}

export function routeSummary(groups, state) {
  const visible = groups.filter(group => (!state.selected.length || state.selected[0] === group.key) && (!state.available || group.available > 0));
  return { keys: visible.map(group => group.key), products: visible.reduce((sum, group) => sum + (state.available ? group.available : group.total), 0), brands: visible.length, countries: new Set(visible.map(group => group.country).filter(Boolean)).size };
}

export function routeStatusText(groups, result, labels = {}) {
  const {discoveries = 'descubrimientos', brandOne = 'marca', brandOther = 'marcas', countryOne = 'país', countryOther = 'países'} = labels;
  const known = result.brands > 0 && groups.filter(group => result.keys.includes(group.key)).every(group => group.country);
  return `${result.products} ${discoveries} · ${result.brands} ${result.brands === 1 ? brandOne : brandOther}${known ? ` · ${result.countries} ${result.countries === 1 ? countryOne : countryOther}` : ''}`;
}

if (typeof customElements !== 'undefined' && !customElements.get('brand-route')) {
  customElements.define('brand-route', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.chapters = [...this.querySelectorAll('[data-brand]')];
      this.groups = this.chapters.map(chapter => ({ key: chapter.dataset.brand, country: chapter.dataset.country, total: Number(chapter.dataset.total), available: Number(chapter.dataset.available) }));
      this.keys = this.groups.map(group => group.key);
      this.buttons = [...this.querySelectorAll('[data-select-brand]')];
      this.availability = this.querySelector('[data-only-available]');
      this.state = readRouteState(location.search, this.keys);
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.querySelectorAll('[data-route-controls]').forEach(control => { control.hidden = false; });
      this.cleanupEditorial = setupBrandEditorial(this, options);
      this.buttons.forEach(button => button.addEventListener('click', () => {
        this.state.selected = selectBrand(this.state.selected, button.dataset.selectBrand);
        this.update(true);
        this.scrollToFirstChapter();
      }, options));
      this.availability.addEventListener('change', () => {
        this.state.available = this.availability.checked;
        this.update(true);
      }, options);
      this.querySelector('[data-reset-route]').addEventListener('click', () => {
        this.state = { selected: [], available: false };
        this.update(true);
        this.buttons[0].focus({ preventScroll: true });
        this.scrollToFirstChapter();
      }, options);
      window.addEventListener('popstate', () => {
        this.state = readRouteState(location.search, this.keys);
        this.update(false);
      }, options);
      this.setAttribute('data-route-enhanced', '');
      this.update(false);
    }
    disconnectedCallback() { this.cleanupEditorial?.(); this.abort?.abort(); cancelAnimationFrame(this.scrollFrame); this.initialized = false; }
    scrollToFirstChapter() {
      cancelAnimationFrame(this.scrollFrame);
      // Measure after filtering: the bar can change height when its count wraps.
      this.scrollFrame = requestAnimationFrame(() => {
        const target = this.chapters.find(chapter => !chapter.hidden) || this.querySelector('[data-route-empty]');
        const navigator = this.querySelector('[data-route-navigator]');
        if (!target || !navigator || !this.isConnected) return;
        const navStyle = getComputedStyle(navigator);
        const bottomBar = navStyle.position === 'fixed' && navStyle.bottom !== 'auto';
        const top = parseFloat(navStyle.top) || 0;
        let scrollRoot = target.parentElement;
        while (scrollRoot && !/(auto|scroll)/.test(getComputedStyle(scrollRoot).overflowY)) scrollRoot = scrollRoot.parentElement;
        // The mobile theme already reserves header space on the document.
        const padding = parseFloat(getComputedStyle(scrollRoot || document.documentElement).scrollPaddingTop) || 0;
        target.style.scrollMarginTop = `${bottomBar ? 12 : Math.max(0, top + navigator.getBoundingClientRect().height + 12 - padding)}px`;
        // Native scrolling handles both the desktop page wrapper and mobile document.
        target.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'instant' });
      });
    }
    update(writeHistory) {
      const result = routeSummary(this.groups, this.state);
      const selectedBrand = this.state.selected[0] || '';
      this.availability.checked = this.state.available;
      this.buttons.forEach(button => {
        const key = button.dataset.selectBrand;
        button.setAttribute('aria-pressed', String(key ? this.state.selected.includes(key) : !this.state.selected.length));
      });
      this.chapters.forEach(chapter => {
        chapter.hidden = !result.keys.includes(chapter.dataset.brand);
        // A hidden brand must never keep playing in the background.
        if (chapter.hidden) chapter.querySelectorAll('.cx-route__profile-video video').forEach(video => video.pause());
        const history = chapter.querySelector('.cx-route__brand-history');
        if (history && (selectedBrand === chapter.dataset.brand || this.selectedBrand === chapter.dataset.brand)) {
          history.open = selectedBrand === chapter.dataset.brand;
        }
        updateBrandCollection(chapter, this.state.available);
        chapter.querySelector('[data-brand-count]').textContent = this.state.available ? chapter.dataset.available : chapter.dataset.total;
      });
      const text = routeStatusText(this.groups, result, this.dataset);
      this.selectedBrand = selectedBrand;
      this.querySelector('[data-route-status]').textContent = text;
      this.querySelector('[data-route-footer-status]').textContent = text;
      this.querySelector('[data-route-empty]').hidden = result.products !== 0;
      if (writeHistory) {
        const url = new URL(location.href);
        this.state.selected.length ? url.searchParams.set('marcas', this.state.selected.join(',')) : url.searchParams.delete('marcas');
        this.state.available ? url.searchParams.set('disponibles', '1') : url.searchParams.delete('disponibles');
        if (url.href !== location.href) history.pushState(null, '', url);
      }
    }
  });
}
